import { TenantConfiguration } from '@/features/system/store/types/TenantConfiguration';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { selectSystemTenantConfig, selectSystemTenantConfigurationIsLoading } from '@/features/system/store/selectors';
import { ConditionGuard } from '@/components/guards/ConditionGuard/ConditionGuard';
import { TenantLogo } from '@/components/ui/images/DisplayTenantLogo/DisplayTenantLogo';
import { RootPaths } from '@/routes/paths';
import { useSelector } from '@/store/hooks';
import {
  BackgroundImage,
  Container,
  Content,
  DefaultLogo,
  FormContainer,
  TextContainer,
  ftSeriateLogo,
} from './AuthPageContainer.styles';

type AuthPageContainerProps = PropsWithChildren<object>;

export const AuthPageContainer = ({ children }: AuthPageContainerProps) => {
  const tenantConfigurationIsLoading = useSelector(selectSystemTenantConfigurationIsLoading);
  const tenantConfiguration: TenantConfiguration | null = useSelector(selectSystemTenantConfig);
  const location = useLocation();

  const welcomeText = useMemo(() => {
    if (tenantConfiguration?.pages?.login?.welcomeText) return tenantConfiguration.pages.login.welcomeText;
    return tenantConfigurationIsLoading ? '' : 'Seriate by FastTrack';
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.welcomeText]);

  const descriptionText = useMemo(() => {
    if (tenantConfiguration?.pages?.login?.descriptionText) return tenantConfiguration.pages.login.descriptionText;
    return '';
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.descriptionText]);

  const bottomBackgroundImage = useMemo(() => {
    if (tenantConfiguration?.pages.login.bottomBackgroundImage)
      return tenantConfiguration.pages.login.bottomBackgroundImage;
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.bottomBackgroundImage]);

  const bottomBackgroundColor = useMemo(() => {
    if (tenantConfiguration?.pages.login.bottomBackgroundColor)
      return tenantConfiguration.pages.login.bottomBackgroundColor;
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.bottomBackgroundColor]);

  return (
    <Container backgroundColor={bottomBackgroundColor} backgroundImage={bottomBackgroundImage}>
      <BackgroundImage
        backgroundImage={tenantConfiguration?.pages?.login?.topBackgroundImage}
        backgroundColor={tenantConfiguration?.pages?.login?.topBackgroundColor}
      />
      <ConditionGuard condition={!tenantConfigurationIsLoading}>
        <Content>
          <TextContainer isOtp={location.pathname === RootPaths.OTP}>
            <Typography data-testid="PageContainerTitle" variant="h1">
              {welcomeText}
            </Typography>
            <ConditionGuard condition={Boolean(descriptionText)}>
              <Typography data-testid="PageContainerTitleDescription" variant="h4">
                {descriptionText}
              </Typography>
            </ConditionGuard>
          </TextContainer>
          <FormContainer>
            <TenantLogo
              isLoginPage
              fallbackLogo={<DefaultLogo id="login-logo" src={ftSeriateLogo} alt="ftSeriate" />}
            />
            {children}
          </FormContainer>
        </Content>
      </ConditionGuard>
    </Container>
  );
};
